import {styled} from '@linaria/react';
import React, {FC, SyntheticEvent, useCallback, useId, useState} from 'react';
import {Z_INDEXES} from '../../../constants/layers';
import {SIZE} from '../../../constants/sizing';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {UnstyledButton} from '../../atoms/Buttons/UnstyledButton';
import {
  ContentPopper,
  ContentPopperProps,
} from '../../atoms/ContentPopper/ContentPopper';
import {SecondaryContentPopperButton} from '../../atoms/ContentPopper/ContentPopperButtons/ContentPopperButtons';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {tabFocusedStyles} from '../../atoms/TabFocused/tabFocusedStyles';
import {
  TermsAndConditionsBody,
  TermsAndConditionsHeader,
} from '../../atoms/TermsAndConditions/TermsAndConditions';
import {UnstyledLinkLinaria} from '../../atoms/UnstyledLink/UnstyledLinkLinaria';
import {useCountdown} from '../../molecules/Countdown/useCountdown';
import {RowSection} from '../../molecules/SplitView/RowSection';

export interface DiscountBannerProps {
  headline: string;
  endsAt?: Date;
  link?: string;
  onClick?: VoidFunction;
  conditionText?: string;
  foregroundColor: string;
  backgroundColor: string;
  countdownForegroundColor: string;
  countdownBackgroundColor: string;
  reducedSize?: boolean;
  infoButtonLabel: string;
  closeButtonLabel: string;
  ContentPopperLayoutComponent: ContentPopperProps['LayoutComponent'];
  className?: string;
  ['data-testid']?: string;
}

export const DiscountBanner: FC<DiscountBannerProps> = ({
  headline,
  endsAt,
  link,
  onClick,
  conditionText,
  foregroundColor,
  backgroundColor,
  countdownForegroundColor,
  countdownBackgroundColor,
  reducedSize = false,
  infoButtonLabel,
  closeButtonLabel,
  ContentPopperLayoutComponent,
  className,
  'data-testid': dataTestid = 'discountTopBanner',
}) => {
  const [infoOpen, setInfoOpen] = useState(false);
  const dialogId = useId();

  const openInfo = useCallback((e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setInfoOpen(true);
  }, []);

  const closeInfo = useCallback(() => {
    setInfoOpen(false);
  }, []);

  return (
    <BannerRow
      className={className}
      data-testid={dataTestid}
      data-reduced-size={reducedSize ? '' : undefined}
      style={{backgroundColor}}
    >
      <RowSection constrained={reducedSize}>
        <ContentRow>
          {!reducedSize && endsAt && (
            <CountdownPill
              endsAt={endsAt}
              foregroundColor={countdownForegroundColor}
              backgroundColor={countdownBackgroundColor}
              data-testid={`${dataTestid}-countdown`}
            />
          )}

          <BannerHeadline
            headline={headline}
            link={link}
            onClick={onClick}
            foregroundColor={foregroundColor}
            data-testid={`${dataTestid}-headline`}
          />

          {conditionText && (
            <InfoButton
              aria-label={infoButtonLabel}
              aria-haspopup="dialog"
              aria-expanded={infoOpen}
              aria-controls={dialogId}
              onClick={openInfo}
              data-testid={`${dataTestid}-infoIcon`}
            >
              <Icon
                asset={GenericIconName.Info}
                size="SS"
                fill={foregroundColor}
              />
            </InfoButton>
          )}
        </ContentRow>
      </RowSection>
      {conditionText && (
        <div id={dialogId}>
          <ContentPopper
            LayoutComponent={ContentPopperLayoutComponent}
            open={infoOpen}
            onClose={closeInfo}
            renderInPortal
            headerContent={
              <TermsAndConditionsHeader>{headline}</TermsAndConditionsHeader>
            }
            bodyContent={
              <TermsAndConditionsBody>{conditionText}</TermsAndConditionsBody>
            }
            footerContent={
              <SecondaryContentPopperButton
                label={closeButtonLabel}
                onClick={closeInfo}
              />
            }
          />
        </div>
      )}
    </BannerRow>
  );
};

interface BannerHeadlineProps {
  headline: string;
  link?: string;
  onClick?: VoidFunction;
  foregroundColor: string;
  ['data-testid']: string;
}

const BannerHeadline: FC<BannerHeadlineProps> = ({
  headline,
  link,
  onClick,
  foregroundColor,
  'data-testid': dataTestid,
}) => {
  if (link) {
    return (
      <HeadlineLink
        to={link}
        onClick={onClick}
        aria-label={undefined}
        data-testid={dataTestid}
        style={{color: foregroundColor}}
      >
        <Headline>{headline}</Headline>
      </HeadlineLink>
    );
  }

  if (onClick) {
    return (
      <HeadlineButton
        aria-label={headline}
        onClick={onClick}
        data-testid={dataTestid}
        style={{color: foregroundColor}}
      >
        <Headline>{headline}</Headline>
      </HeadlineButton>
    );
  }

  return (
    <HeadlineBlock data-testid={dataTestid} style={{color: foregroundColor}}>
      <Headline>{headline}</Headline>
    </HeadlineBlock>
  );
};

interface CountdownPillProps {
  endsAt: Date;
  foregroundColor: string;
  backgroundColor: string;
  ['data-testid']: string;
}

const CountdownPill: FC<CountdownPillProps> = ({
  endsAt,
  foregroundColor,
  backgroundColor,
  'data-testid': dataTestid,
}) => {
  const countdown = useCountdown(endsAt);
  const [days, hours, minutes, seconds] = countdown;

  if (countdown.hideCountdown) {
    return null;
  }

  const label =
    days === '00'
      ? `${hours}H ${minutes}M ${seconds}S`
      : `${days}D ${hours}H ${minutes}M ${seconds}S`;

  return (
    <Pill
      data-testid={dataTestid}
      aria-label={label}
      style={{backgroundColor, color: foregroundColor}}
    >
      <PillIconBox>
        <Icon
          asset={GenericIconName.Clock}
          width="100%"
          height="100%"
          fill={foregroundColor}
        />
      </PillIconBox>
      <PillLabel>{label}</PillLabel>
    </Pill>
  );
};

const BannerRow = styled.div`
  position: relative;
  z-index: ${Z_INDEXES.DISCOUNT_BANNER};
  display: flex;
  align-items: center;
  height: ${SIZE.U11};

  &[data-reduced-size] {
    height: ${SIZE.U9};
    padding-inline: ${SIZE.U6};
  }
`;

const ContentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SIZE.U4};
  width: 100%;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: ${SIZE.U05};
  height: ${SIZE.U6};
  padding-inline: ${SIZE.U2};
  border-radius: 999px;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  white-space: nowrap;
`;

const PillIconBox = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  width: ${SIZE.U4};
  height: ${SIZE.U4};
`;

const PillLabel = styled.span`
  font-size: ${FONT_SIZES.px12};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: 18px;
  font-variant-numeric: tabular-nums;
  color: inherit;
`;

const headlineBlockCss = `
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-width: 0;
  height: ${SIZE.U6};
  max-width: 1000px;
  margin-inline: auto;
`;

const HeadlineBlock = styled.div`
  ${headlineBlockCss}
`;

const HeadlineButton = styled(UnstyledButton)`
  ${headlineBlockCss}
  cursor: pointer;

  &:focus-visible {
    ${tabFocusedStyles()}
  }
`;

const HeadlineLink = styled(UnstyledLinkLinaria)`
  ${headlineBlockCss}

  &:focus-visible {
    ${tabFocusedStyles()}
  }
`;

const Headline = styled.span`
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: 20px;
  color: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const InfoButton = styled(UnstyledButton)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${SIZE.U6};
  height: ${SIZE.U6};

  &:focus-visible {
    ${tabFocusedStyles({offset: '-2px'})}
  }
`;
